import { ref, computed } from "vue";

export function useChannelManager(toast: any) {
  const whatsAppChannel = ref<any>(null);
  const linkedInChannel = ref<any>(null);
  const emailChannel = ref<any>(null);
  const emailAccounts = ref<any[]>([]);
  const isChannelsLoading = ref(true);
  const showConnectChannelModal = ref(false);
  const channelToConnect = ref<any>(null);
  const isConnectingChannel = ref(false);

  const isEmailAccountConnected = computed(() => {
    if (!emailChannel.value || emailChannel.value.type !== "email") {
      return false;
    }
    if (!emailChannel.value.status) {
      return false;
    }
    const status = emailChannel.value.status.toUpperCase();
    return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
  });

  const availableChannelsForChat = computed(() => {
    const channels = [];
    if (whatsAppChannel.value?.accountId) {
      channels.push({
        id: whatsAppChannel.value.id,
        name: "WhatsApp",
        type: "whatsapp",
        icon: "mdi:whatsapp",
        accountId: whatsAppChannel.value.accountId,
        isConnected: isChannelHealthyStatus(whatsAppChannel.value?.status),
        status: whatsAppChannel.value.status,
      });
    }
    if (linkedInChannel.value?.accountId) {
      channels.push({
        id: linkedInChannel.value.id,
        name: "LinkedIn",
        type: "linkedin",
        icon: "mdi:linkedin",
        accountId: linkedInChannel.value.accountId,
        isConnected: isChannelHealthyStatus(linkedInChannel.value?.status),
        status: linkedInChannel.value.status,
      });
    }
    return channels;
  });

  function isChannelHealthyStatus(status: string): boolean {
    if (!status) return false;
    const s = String(status).toUpperCase();
    return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(s);
  }

  async function loadChannels() {
    isChannelsLoading.value = true;
    try {
      const Unipile = (await import("@/services/unipile/unipile")).default;
      const response = await Unipile.getConnectedChannels();
      const channels = response.data.data || [];

      whatsAppChannel.value = channels.find((c: any) => c.type === "whatsapp") || null;
      linkedInChannel.value = channels.find((c: any) => c.type === "linkedin") || null;

      const emailAccountsData = channels.filter((c: any) => c.type === "email");
      emailAccounts.value = emailAccountsData.map((account: any) => ({
        id: account.accountId,
        email: account.providerAccountId || account.name || "",
        name: account.providerAccountId || account.name || "",
        accountType: account.accountType,
        status: account.status,
        icon: account.icon,
      }));

      emailChannel.value = emailAccountsData[0] || null;
    } catch (error) {
      console.error("Failed to load channels:", error);
    } finally {
      isChannelsLoading.value = false;
    }
  }

  function getChannelForType(channelType: string): any {
    switch (channelType) {
      case "whatsapp":
        return whatsAppChannel.value;
      case "linkedin":
        return linkedInChannel.value;
      case "email":
        return emailChannel.value;
      default:
        return null;
    }
  }

  function handleConnectEmailAccount() {
    channelToConnect.value = {
      type: "email",
      name: "Email",
      icon: "mdi:email-outline",
    };
    showConnectChannelModal.value = true;
  }

  function handleReconnectLinkedIn() {
    channelToConnect.value = {
      type: "linkedin",
      name: "LinkedIn",
      icon: "mdi:linkedin",
    };
    showConnectChannelModal.value = true;
  }

  function handleReconnectWhatsApp() {
    channelToConnect.value = {
      type: "whatsapp",
      name: "WhatsApp",
      icon: "mdi:whatsapp",
    };
    showConnectChannelModal.value = true;
  }

  async function handleConnectChannel(channel: any, accountType?: string) {
    try {
      isConnectingChannel.value = true;
      const request: any = {
        channel: channel.type,
      };

      if (channel.type === "email" && accountType) {
        request.accountType = accountType;
      }

      const Unipile = (await import("@/services/unipile/unipile")).default;
      const response = await Unipile.connectChannel(request);

      if (response.data?.status && response.data?.data?.authUrl) {
        window.location.href = response.data.data.authUrl;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await loadChannels();
        showConnectChannelModal.value = false;
      }
    } catch (error) {
      console.error("Failed to connect channel:", error);
      toast.error((error as any).response?.data?.message || "Failed to initiate connection");
    } finally {
      isConnectingChannel.value = false;
    }
  }

  return {
    whatsAppChannel,
    linkedInChannel,
    emailChannel,
    emailAccounts,
    isChannelsLoading,
    showConnectChannelModal,
    channelToConnect,
    isConnectingChannel,
    isEmailAccountConnected,
    availableChannelsForChat,
    isChannelHealthyStatus,
    loadChannels,
    getChannelForType,
    handleConnectEmailAccount,
    handleReconnectLinkedIn,
    handleReconnectWhatsApp,
    handleConnectChannel,
  };
}
